/**
 * Help JSON is keyed by the Alloy Property title shown in the table
 * (e.g. "Electrical Conductivity WQ-T6-PS (%IACS)").
 *
 * Matching is intentionally tolerant of common DB renames:
 *   Ave ↔ Avg ↔ Average, "190 °C" ↔ "190°C", Aluminium ↔ Aluminum, etc.
 *
 * Supported value shapes:
 *   "Property Name": "Help text"
 *   "Property Name": { "description": "Help text", "aliases": ["Old Name"] }
 */

function readHelpDescription(entry) {
  if (typeof entry === "string") {
    return entry.trim()
  }
  if (entry && typeof entry === "object") {
    return String(entry.description || entry.help || "").trim()
  }
  return ""
}

function readHelpAliases(entry) {
  if (!entry || typeof entry !== "object") return []
  const raw = entry.aliases || entry.alias || []
  if (typeof raw === "string") return [raw]
  if (Array.isArray(raw)) return raw.map((v) => String(v || "").trim()).filter(Boolean)
  return []
}

/**
 * Normalize property labels so tiny Unicode / spelling differences still match.
 */
export function normalizeHelpKey(value) {
  return String(value || "")
    .normalize("NFKC")
    .replace(/\u2103/g, "°C") // ℃
    .replace(/[\u00B0\u02DA\u00BA]/g, "°") // ° ˚ º
    .replace(/[\u03B2\u0392]/g, "β") // β Β
    .replace(/[\u03B1\u0391]/g, "α") // α Α
    .replace(/[\u2000-\u200B\u202F\u00A0\uFEFF]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    // common DB spelling / abbreviation drift
    .replace(/\baluminium\b/g, "aluminum")
    .replace(/\bave\b/g, "avg")
    .replace(/\baverage\b/g, "avg")
    // "190 °c" / "190° c" / "190 ° c" → "190°c"
    .replace(/(\d)\s*°\s*c\b/g, "$1°c")
    // "vol. %" / "vol%" / "vol %" → "vol.%"
    .replace(/\bvol\.?\s*%/g, "vol.%")
    .replace(/\bwt\.?\s*%/g, "wt.%")
    // "% iacs" → "%iacs"
    .replace(/%\s*iacs\b/g, "%iacs")
}

function buildHelpLookup(helpMap) {
  const byNorm = new Map()
  for (const [key, value] of Object.entries(helpMap || {})) {
    const description = readHelpDescription(value)
    if (!description) continue
    const entry = { name: key, title: key, description }
    const keys = [key, ...readHelpAliases(value)]
    for (const label of keys) {
      const norm = normalizeHelpKey(label)
      if (!norm || byNorm.has(norm)) continue
      byNorm.set(norm, entry)
    }
  }
  return byNorm
}

/**
 * Find help text for a table property name.
 */
export function findCastingPropertyHelpEntry(helpMap, propertyName = "") {
  const map = helpMap || {}
  const name = String(propertyName || "").trim()
  if (!name) return null

  if (Object.prototype.hasOwnProperty.call(map, name)) {
    const description = readHelpDescription(map[name])
    if (!description) return null
    return { name, title: name, description }
  }

  const target = normalizeHelpKey(name)
  if (!target) return null

  const lookup = buildHelpLookup(map)
  return lookup.get(target) || null
}

export function resolveCastingPropertyHelp(helpMap, propertyName = "") {
  const entry = findCastingPropertyHelpEntry(helpMap, propertyName)
  if (!entry) return null
  return {
    title: entry.title || propertyName,
    description: entry.description,
  }
}

export function hasCastingPropertyHelp(helpMap, propertyName = "") {
  return findCastingPropertyHelpEntry(helpMap, propertyName) != null
}
